<script setup lang="ts">
/** Global search (Ctrl+K). Results respect the same permissions and scopes as each list screen. */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { Building2, Loader2, MapPinned, Search, Target, UserRound } from 'lucide-vue-next';
import { api } from '@/lib/http';

interface Hit {
  type: 'lead' | 'customer' | 'project' | 'site';
  id: string;
  title: string;
  subtitle: string;
  link: string;
}

const emit = defineEmits<{ close: [] }>();
const router = useRouter();
const q = ref('');
const hits = ref<Hit[]>([]);
const loading = ref(false);
const active = ref(0);
const input = ref<HTMLInputElement | null>(null);
let timer: ReturnType<typeof setTimeout> | undefined;
let seq = 0;

const icons = { lead: Target, customer: UserRound, project: Building2, site: MapPinned };
const labels = { lead: 'Leads', customer: 'Customers', project: 'Projects', site: 'Sites' };
const groups = computed(() => (['lead', 'customer', 'project', 'site'] as const).map((t) => ({ type: t, items: hits.value.filter((h) => h.type === t) })).filter((g) => g.items.length));

watch(q, (value) => {
  clearTimeout(timer);
  active.value = 0;
  if (value.trim().length < 2) {
    hits.value = [];
    loading.value = false;
    return;
  }
  // Show the spinner straight away so "No matches" never flashes before results arrive.
  loading.value = true;
  timer = setTimeout(async () => {
    const mine = ++seq;
    loading.value = true;
    try {
      const res = await api.get<Hit[]>('/search', { params: { q: value.trim() } });
      if (mine === seq) hits.value = res;
    } catch {
      if (mine === seq) hits.value = [];
    } finally {
      if (mine === seq) loading.value = false;
    }
  }, 250);
});

function go(h: Hit) {
  emit('close');
  void router.push(h.link);
}

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') emit('close');
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    active.value = Math.min(active.value + 1, hits.value.length - 1);
  }
  if (e.key === 'ArrowUp') {
    e.preventDefault();
    active.value = Math.max(active.value - 1, 0);
  }
  if (e.key === 'Enter' && hits.value[active.value]) go(hits.value[active.value]!);
}
const flatIndex = (h: Hit) => hits.value.indexOf(h);

onMounted(async () => {
  window.addEventListener('keydown', onKey);
  await nextTick();
  input.value?.focus();
});
onBeforeUnmount(() => window.removeEventListener('keydown', onKey));
</script>

<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[10vh]" role="dialog" aria-modal="true" aria-label="Search">
      <div class="absolute inset-0 bg-navy-950/50" @click="emit('close')" />
      <div class="card relative w-full max-w-xl overflow-hidden shadow-pop">
        <div class="flex items-center gap-3 border-b border-base px-4">
          <Search class="h-5 w-5 text-muted" />
          <input ref="input" v-model="q" class="h-14 flex-1 bg-transparent text-base outline-none" placeholder="Search by name, phone, number, site code…" aria-label="Search" />
          <Loader2 v-if="loading" class="h-4 w-4 animate-spin text-muted" />
          <kbd class="rounded border border-base px-1.5 text-[10px] text-muted">Esc</kbd>
        </div>
        <div class="max-h-[60vh] overflow-y-auto py-2">
          <p v-if="q.trim().length < 2" class="px-4 py-6 text-center text-sm text-muted">Type at least two characters.</p>
          <p v-else-if="loading && !hits.length" class="px-4 py-6 text-center text-sm text-muted">Searching…</p>
          <p v-else-if="!hits.length" class="px-4 py-6 text-center text-sm text-muted">No matches.</p>
          <div v-for="g in groups" :key="g.type" class="mb-1">
            <p class="px-4 py-1 text-[10px] font-semibold uppercase tracking-widest text-muted">{{ labels[g.type] }}</p>
            <button
              v-for="h in g.items"
              :key="h.id"
              type="button"
              class="flex w-full items-center gap-3 px-4 py-2 text-left"
              :class="flatIndex(h) === active ? 'bg-navy-50 dark:bg-navy-900/40' : 'hover:bg-slate-50 dark:hover:bg-white/5'"
              @mouseenter="active = flatIndex(h)"
              @click="go(h)"
            >
              <component :is="icons[h.type]" class="h-4 w-4 shrink-0 text-navy-500" />
              <span class="min-w-0">
                <span class="block truncate text-sm font-medium">{{ h.title }}</span>
                <span class="block truncate text-xs text-muted">{{ h.subtitle }}</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
